'use client';

import {
  arrow,
  autoUpdate,
  FloatingArrow,
  FloatingPortal,
  flip,
  OffsetOptions,
  offset,
  Placement,
  Strategy,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react';
import {AnimatePresence, motion} from 'motion/react';
import {cloneElement, ReactElement, ReactNode, useRef, useState} from 'react';
import {cn, getMotionProps} from '../../libs';

type TooltipReferenceProps = {
  'data-open'?: boolean;
  ref?: React.Ref<Element>;
};

interface TooltipProps {
  children: ReactElement;
  className?: string;
  color?: 'bg-secondary';
  message: ReactNode;
  enabled?: boolean;
  placement?: Placement;
  distance?: OffsetOptions;
  delay?:
    | number
    | Partial<{
        open: number;
        close: number;
      }>;
  closeOnReferencePress?: boolean;
  mountRoot?: HTMLElement;
  alwaysShow?: boolean;
  showArrow?: boolean;
  strategy?: Strategy;
}

const getFloatingMotionProps = (placement: Placement | undefined) => {
  const y = placement?.includes('top') ? 4 : -4;
  return getMotionProps({
    initial: {y, scale: 0.98, opacity: 0},
    animate: {y: 0, scale: 1, opacity: 1},
    exit: {y, scale: 0.98, opacity: 0},
  });
};

export const Tooltip = ({
  className,
  children,
  message,
  enabled = true,
  showArrow = false,
  alwaysShow = false,
  placement = 'top',
  distance = 8,
  delay = {open: 100, close: 100},
  closeOnReferencePress = false,
  strategy,
  mountRoot,
}: TooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef<SVGSVGElement>(null);
  const {
    refs,
    floatingStyles,
    placement: floatingPlacement,
    context,
  } = useFloating({
    open: isOpen,
    placement,
    strategy,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(distance),
      flip(),
      shift({padding: 8}),
      showArrow && arrow({element: arrowRef, padding: 4}),
    ],
    onOpenChange: setIsOpen,
  });

  const {getReferenceProps, getFloatingProps} = useInteractions([
    useHover(context, {
      delay,
      enabled,
      handleClose: safePolygon({
        requireIntent: true,
      }),
    }),
    useDismiss(context, {
      referencePress: closeOnReferencePress,
    }),
  ]);

  const referenceElement = children as ReactElement<TooltipReferenceProps>;

  return (
    <>
      {cloneElement(referenceElement, {
        ref: refs.setReference,
        ...getReferenceProps(),
        'data-open': enabled && (isOpen || alwaysShow),
      })}

      <AnimatePresence>
        {enabled && (isOpen || alwaysShow) && (
          <FloatingPortal root={mountRoot}>
            <div
              className="z-1000"
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <motion.div
                className={cn(
                  'w-max rounded-md bg-bg-secondary px-3 py-2 text-bg-secondary text-sm shadow-tooltip',
                  className,
                )}
                {...getFloatingMotionProps(floatingPlacement)}
              >
                {showArrow && (
                  <FloatingArrow fill="currentColor" ref={arrowRef} context={context} />
                )}
                <div className="text-text-primary">{message}</div>
              </motion.div>
            </div>
          </FloatingPortal>
        )}
      </AnimatePresence>
    </>
  );
};
